import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDiario } from '@hooks/useDiario.js'
import { useContatos } from '@hooks/useContatos.js'
import { MODULOS } from '@utils/constants.js'
import { CardListSkeleton } from '@components/primitives/Skeleton.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { NavIcon } from '@components/layout/NavIcons.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { DiarioEntradaRapida } from '@components/diario/DiarioEntradaRapida.js'
import { DiarioItemCard } from '@components/diario/DiarioItemCard.js'
import { exportarDiarioPDF } from '@services/backup.js'

type FiltroRapido = 'todos' | 'semana' | 'com-passo' | 'soltas'

const FILTROS_RAPIDOS: { id: FiltroRapido; label: string }[] = [
  { id: 'todos', label: 'Todas' },
  { id: 'com-passo', label: 'Com etapa' },
  { id: 'soltas', label: 'Sem vínculo' },
]

function applyFiltroRapido(
  items: ReturnType<typeof useDiario>['itens'],
  filtro: FiltroRapido,
): ReturnType<typeof useDiario>['itens'] {
  if (filtro === 'todos') return items
  if (filtro === 'com-passo') return items.filter((i) => !!i.topicoTitulo)
  if (filtro === 'soltas') return items.filter((i) => !i.topicoTitulo)

  const now = new Date()
  const start = new Date(now)
  start.setDate(now.getDate() - now.getDay())
  start.setHours(0, 0, 0, 0)
  return items.filter((i) => new Date(i.criadoEm) >= start)
}

export function DiarioPage() {
  const { itens, todos, filtros, setFiltros, isLoading, registrar, remover, editar } = useDiario()
  const { todos: contatos } = useContatos()
  const [searchParams] = useSearchParams()
  const autoFoto = searchParams.get('foto') === '1'
  const [filtroRapido, setFiltroRapido] = useState<FiltroRapido>('todos')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const itensFiltrados = applyFiltroRapido(itens, filtroRapido)
  const semFiltros = !filtros.busca && !filtros.moduloId

  function setFiltro<K extends keyof typeof filtros>(key: K, value: (typeof filtros)[K]) {
    setFiltros((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <PageShell>
      <ModePageHeader
        title='Diário de bordo'
        subtitle='Foto ou vídeo, clima, quem trabalhou e o que aconteceu. É a sua prova em caso de disputa.'
      />

      <section className='cf3-block' id='diario-entrada'>
        <DiarioEntradaRapida onSalvar={registrar} equipe={contatos} autoFoto={autoFoto} />
      </section>

      <div className='cf3-segment cf3-segment--full' role='group' aria-label='Filtrar entradas'>
        {FILTROS_RAPIDOS.map((f) => (
          <button
            key={f.id}
            type='button'
            className={`cf3-segment__btn${filtroRapido === f.id ? ' cf3-segment__btn--active' : ''}`}
            onClick={() => setFiltroRapido(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <section className='cf3-block cf3-diario-filters cf3-diario-filters--stack'>
        <input
          type='search'
          className='cf-input'
          placeholder='Buscar no diário…'
          value={filtros.busca}
          onChange={(e) => setFiltro('busca', e.target.value)}
          aria-label='Buscar no diário'
        />
        <select
          className='cf-input'
          value={filtros.moduloId}
          onChange={(e) => setFiltro('moduloId', e.target.value)}
          aria-label='Filtrar por módulo'
        >
          <option value=''>Todos os módulos</option>
          {MODULOS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.titulo}
            </option>
          ))}
        </select>
      </section>

      <section className='cf3-block'>
        {isLoading && <CardListSkeleton count={4} label='Carregando diário…' />}

        {!isLoading && todos.length === 0 && (
          <EmptyPanel
            icon={<NavIcon icon='diario' size={24} />}
            title='Nenhuma anotação ainda'
            description='Registre visitas, decisões e ocorrências do canteiro. Use o formulário abaixo ou volte depois de uma vistoria.'
            actionLabel='Ir para o formulário'
            onAction={() =>
              document.getElementById('diario-entrada')?.scrollIntoView({ behavior: 'smooth' })
            }
          />
        )}

        {!isLoading && todos.length > 0 && itensFiltrados.length === 0 && (
          <EmptyPanel
            icon={<NavIcon icon='diario' size={24} />}
            title='Nenhum resultado'
            description='Nenhuma anotação corresponde aos filtros atuais. Limpe a busca ou escolha outro período.'
            actionLabel='Limpar filtros'
            onAction={() => {
              setFiltros({ busca: '', moduloId: '', mes: '' })
              setFiltroRapido('todos')
            }}
          />
        )}

        {!isLoading && itensFiltrados.length > 0 && (
          <>
            <div className='cf3-diario-toolbar'>
              <span className='cf3-diario-toolbar__count'>
                {semFiltros && filtroRapido === 'todos'
                  ? `${todos.length} anotaç${todos.length === 1 ? 'ão' : 'ões'}`
                  : `${itensFiltrados.length} de ${todos.length} anotações`}
              </span>
              <button
                type='button'
                className='cf3-insight__link cf3-diario-toolbar__export'
                onClick={() => void exportarDiarioPDF()}
              >
                Exportar diário de bordo
              </button>
            </div>
            <div className='cf3-diario-timeline'>
              {itensFiltrados.map((item) => (
                <DiarioItemCard
                  key={item.id}
                  item={item}
                  equipe={contatos}
                  expandido={expandedId === item.id}
                  onToggle={() => setExpandedId((id) => (id === item.id ? null : item.id))}
                  onRemover={remover}
                  onEditar={editar}
                />
              ))}
            </div>
          </>
        )}
      </section>
    </PageShell>
  )
}
